import { useState, useCallback, useRef } from "react";
import { FileUploadState } from "datasets/pages/tables/components/file-validation/types";
import { resolveObjectKey } from "files/hooks/useFiles";
import BaseDirectory from "files/types/enums/baseDirectory";
import FileType from "files/types/enums/fileType";
import FileInfo from "files/types/interfaces/fileInfo";
import useUser from "~/contexts/user";

const { VITE_RWB_API } = import.meta.env;
const MULTIPART_LIMIT = 50 * 1024 * 1024; // 50 MB
const VALIDATION_UPLOAD_FOLDER = "ingestions/uploads"; // Folder for ingestion file uploads

// Matches virtual-hosted hostname, e.g. my-bucket.s3.us-east-1.amazonaws.com
const S3_VIRTUAL_HOSTED_HOST_RE = /^([^.]+)\.s3(?:[.-][a-z]{2}-[a-z]+-\d+)?\.amazonaws\.com$/;
// Matches path-style hostname, e.g. s3.us-east-2.amazonaws.com (bucket is first path segment)
const S3_PATH_STYLE_HOST_RE = /^s3(?:[.-][a-z]{2}-[a-z]+-\d+)?\.amazonaws\.com$/;

function extractBucketFromPresignedUrl(url: string): string | undefined {
  try {
    const { hostname, pathname } = new URL(url);
    const virtualHosted = S3_VIRTUAL_HOSTED_HOST_RE.exec(hostname);
    if (virtualHosted) return virtualHosted[1];
    if (S3_PATH_STYLE_HOST_RE.test(hostname)) return pathname.split("/")[1] || undefined;
  } catch {
    // ignore malformed URLs
  }
  return undefined;
}

interface UploadPart {
  etag: string;
  part_num: number;
}

/**
 * Splits a file into chunks for multipart upload
 */
const splitFileIntoChunks = (file: File): Blob[] => {
  const maxChunks = 10000;
  const minChunkSize = 15 * 1024 * 1024; // 15 MB
  const chunkSize = Math.max(minChunkSize, file.size / maxChunks);
  const chunks: Blob[] = [];
  for (let start = 0; start < file.size; start += chunkSize) {
    const end = Math.min(start + chunkSize, file.size);
    chunks.push(file.slice(start, end));
  }
  return chunks;
};

export const useFileUpload = () => {
  const user = useUser();
  const [uploadState, setUploadState] = useState<FileUploadState>({
    selectedFile: null,
    isUploading: false,
  });
  const [uploadError, setUploadError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Uploads a single chunk and tracks progress
   */
  const uploadChunk = useCallback(
    (
      url: string,
      chunk: Blob,
      file: File,
      onProgress: (loaded: number) => void,
      retry = { attemptsLeft: 3, delay: 1500 }
    ): Promise<string> => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", url);
        xhr.setRequestHeader("Content-Type", file.type);

        let prevLoaded = 0;

        xhr.upload.onprogress = (e) => {
          if (!e.lengthComputable) {
            return;
          }
          const delta = e.loaded - prevLoaded;
          onProgress(delta);
          prevLoaded = e.loaded;
        };

        const handleRetry = () =>
          setTimeout(() => {
            uploadChunk(url, chunk, file, onProgress, {
              attemptsLeft: retry.attemptsLeft - 1,
              delay: retry.delay * 2,
            })
              .then(resolve)
              .catch(reject);
          }, retry.delay);

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.getResponseHeader("ETag") || "");
          } else if (retry.attemptsLeft > 0) {
            handleRetry();
          } else {
            reject(new Error(`Upload failed: ${xhr.status}`));
          }
        };

        xhr.onerror = () => {
          if (retry.attemptsLeft > 0) {
            handleRetry();
          } else {
            reject(new Error("Network error"));
          }
        };

        abortControllerRef.current?.signal.addEventListener("abort", () => {
          xhr.abort();
        });
        xhr.send(chunk);
      });
    },
    []
  );

  /**
   * Uploads a file using multipart upload (for files > 50MB)
   */
  const uploadMultipartFile = useCallback(
    async (file: File, key: string): Promise<string | undefined> => {
      const maxParallelReq = 6;
      const chunks = splitFileIntoChunks(file);
      const parts: UploadPart[] = [];
      let completedChunks = 0;
      let bucketFromPresigned: string | undefined;

      // Start multipart upload
      const startResp = await fetch(`${VITE_RWB_API}/file-browser/start-multipart-upload`, {
        method: "POST",
        signal: abortControllerRef.current?.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key, file_type: file.type }),
      });

      if (!startResp.ok) {
        throw new Error("Unable to initiate multipart upload");
      }

      const uploadId = await startResp.json();

      // Upload chunks in parallel batches
      for (let startPartNum = 1; startPartNum <= chunks.length; startPartNum += maxParallelReq) {
        const endPartNum =
          startPartNum + Math.min(maxParallelReq, chunks.length - (startPartNum - 1));

        // Get pre-signed multipart upload URLs
        const presignedResp = await fetch(`${VITE_RWB_API}/file-browser/generate-multipart-urls`, {
          method: "POST",
          signal: abortControllerRef.current?.signal,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            key,
            upload_id: uploadId,
            start: startPartNum,
            end: endPartNum,
          }),
        });

        if (!presignedResp.ok) {
          throw new Error("Unable to generate pre-signed URLs");
        }

        const urls: string[] = await presignedResp.json();

        if (!bucketFromPresigned && urls.length > 0) {
          bucketFromPresigned = extractBucketFromPresignedUrl(urls[0]);
        }

        // Upload chunks in parallel
        await Promise.all(
          urls.map(async (url, idx) => {
            const chunkIndex = startPartNum - 1 + idx;
            const etag = await uploadChunk(url, chunks[chunkIndex], file, (delta) => {
              setUploadState((prev) => {
                const currentProgress = prev.uploadProgress || 0;
                const newProgress = Math.min(currentProgress + (delta / file.size) * 100, 99);
                return { ...prev, uploadProgress: newProgress };
              });
            });
            // Chunk completed -- ensure progress reflects at least this chunk's
            // share of the total. This acts as a fallback when XHR onprogress
            // events don't fire (common with S3 presigned URL PUTs).
            completedChunks++;
            setUploadState((prev) => {
              const chunkFloor = Math.min((completedChunks / chunks.length) * 99, 99);
              const currentProgress = prev.uploadProgress || 0;
              return {
                ...prev,
                uploadProgress: Math.max(currentProgress, chunkFloor),
              };
            });
            parts.push({ etag, part_num: startPartNum + idx });
          })
        );
      }

      // Complete multipart upload
      const sortedParts = parts.sort((a, b) => a.part_num - b.part_num);
      const uniqueParts = Array.from(
        new Map(sortedParts.map((part) => [part.part_num, part])).values()
      );

      const completeResp = await fetch(`${VITE_RWB_API}/file-browser/complete-multipart-upload`, {
        method: "POST",
        signal: abortControllerRef.current?.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          key,
          upload_id: uploadId,
          parts: uniqueParts,
        }),
      });

      if (!completeResp.ok) {
        throw new Error("Unable to complete upload");
      }

      setUploadState((prev) => ({
        ...prev,
        uploadProgress: 100,
      }));

      return bucketFromPresigned ? `s3://${bucketFromPresigned}/${key}` : undefined;
    },
    [uploadChunk]
  );

  /**
   * Uploads a file using single-part upload (for files < 50MB)
   */
  const uploadSingleFile = useCallback(
    async (file: File, key: string): Promise<string | undefined> => {
      const presignedResp = await fetch(`${VITE_RWB_API}/file-browser/generate-upload-url`, {
        method: "POST",
        signal: abortControllerRef.current?.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key, file_type: file.type }),
      });

      if (!presignedResp.ok) {
        throw new Error("Unable to get upload URL");
      }

      const url: string = await presignedResp.json();
      await uploadChunk(url, file, file, (delta) => {
        setUploadState((prev) => {
          const currentProgress = prev.uploadProgress || 0;
          const newProgress = Math.min(currentProgress + (delta / file.size) * 100, 99);
          return { ...prev, uploadProgress: newProgress };
        });
      });
      // Fallback: ensure progress reflects completion even if XHR
      // onprogress events didn't fire (common with S3 presigned URLs)
      setUploadState((prev) => ({
        ...prev,
        uploadProgress: Math.max(prev.uploadProgress || 0, 99),
      }));

      const bucket = extractBucketFromPresignedUrl(url);
      return bucket ? `s3://${bucket}/${key}` : undefined;
    },
    [uploadChunk]
  );

  /**
   * Upload a file from the user's device to their personal bucket
   * @param file - The file to upload
   * @returns FileInfo for the uploaded file
   */
  const uploadFile = useCallback(
    async (file: File): Promise<FileInfo> => {
      if (!user?.email) {
        throw new Error("User email is required for upload");
      }

      // Cancel any existing upload
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      setUploadState((prev) => ({ ...prev, isUploading: true, uploadProgress: 0 }));
      setUploadError(null);

      try {
        // Construct the upload path: scratch/users/{email}/ingestions/uploads/{filename}
        const personalPath = resolveObjectKey(BaseDirectory.PERSONAL, user.email);
        const uploadKey = `${personalPath}/${VALIDATION_UPLOAD_FOLDER}/${file.name}`;

        // Choose upload method based on file size; both return the s3_uri derived
        // from the presigned URL so no extra browse call is needed.
        const uploadFn = file.size < MULTIPART_LIMIT ? uploadSingleFile : uploadMultipartFile;
        const s3Uri = await uploadFn(file, uploadKey);

        const uploadedFileInfo: FileInfo = {
          key: uploadKey,
          type: FileType.FILE,
          size: file.size,
          s3_uri: s3Uri,
        };

        setUploadState((prev) => ({
          ...prev,
          selectedFile: uploadedFileInfo,
          isUploading: false,
          uploadProgress: 100,
        }));

        return uploadedFileInfo;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Upload failed";
        setUploadError(errorMessage);
        setUploadState((prev) => ({ ...prev, isUploading: false, uploadProgress: 0 }));
        throw error;
      } finally {
        abortControllerRef.current = null;
      }
    },
    [user?.email, uploadSingleFile, uploadMultipartFile]
  );

  /**
   * Cancel the current upload
   */
  const cancelUpload = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setUploadState((prev) => ({ ...prev, isUploading: false, uploadProgress: 0 }));
  }, []);

  /**
   * Select an existing file from cloud storage.
   * Accepts a FileInfo object directly from the FileTree.
   */
  const selectFromStorage = useCallback((file: FileInfo): void => {
    setUploadError(null);
    setUploadState((prev) => ({ ...prev, selectedFile: file }));
  }, []);

  /**
   * Reset the upload state to initial values
   */
  const resetUpload = useCallback(() => {
    setUploadState({
      selectedFile: null,
      isUploading: false,
      uploadProgress: undefined,
    });
    setUploadError(null);
  }, []);

  return {
    uploadState,
    uploadFile,
    selectFromStorage,
    resetUpload,
    uploadError,
    cancelUpload,
  };
};
